import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, RotateCcw, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ACADEMIC_YEARS = ['2025-26', '2026-27', '2027-28'];
const DEFAULT_AY = '2026-27';

/**
 * Classes tab — manual add (grade + section + AY), soft delete / reactivate.
 */
export default function ClassesTab({ school, brandColor }) {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(DEFAULT_AY);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ grade: '', section: '', academic_year: DEFAULT_AY });
  const [saving, setSaving] = useState(false);

  const schoolId = school?.school_id;

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/schools/${schoolId}/classes`);
      setClasses(data || []);
    } catch {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.grade.trim() || !form.section.trim()) {
      toast.error('Grade and Section are required'); return;
    }
    setSaving(true);
    try {
      await api.post(`/schools/${schoolId}/classes`, {
        grade: form.grade.trim(),
        section: form.section.trim().toUpperCase(),
        academic_year: form.academic_year,
      });
      toast.success('Class added');
      setAddOpen(false);
      setForm({ grade: '', section: '', academic_year: filterYear });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add class');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    const target = c.status === 'inactive' ? 'active' : 'inactive';
    try {
      await api.patch(`/schools/${schoolId}/classes/${c.school_class_id}`, { status: target });
      toast.success(`Class ${target === 'inactive' ? 'deactivated' : 'reactivated'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  const byYear = classes.filter((c) => c.academic_year === filterYear);
  const active = byYear.filter((c) => c.status !== 'inactive');
  const inactive = byYear.filter((c) => c.status === 'inactive');

  return (
    <div data-testid="classes-tab">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Classes</h2>
          <p className="text-sm text-slate-600">
            Add classes by grade, section, and academic year.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[140px]" data-testid="classes-year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACADEMIC_YEARS.map((ay) => (
                <SelectItem key={ay} value={ay}>{ay}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => { setForm({ grade: '', section: '', academic_year: filterYear }); setAddOpen(true); }}
            style={{ backgroundColor: brandColor }}
            className="text-white"
            data-testid="add-class-btn"
          >
            <Plus size={14} className="mr-1.5" /> Add Class
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500">Loading…</div>
      ) : (
        <>
          {active.length === 0 ? (
            <div
              className="border-2 border-dashed rounded-xl py-16 text-center bg-white/60"
              data-testid="classes-empty"
            >
              <p className="text-slate-600 font-medium">No active classes for {filterYear}</p>
              <p className="text-sm text-slate-500 mt-1">Click "Add Class" to create one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="classes-list">
              {active.map((c) => (
                <div
                  key={c.school_class_id}
                  className="bg-white border rounded-lg px-4 py-3"
                  data-testid={`class-card-${c.school_class_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-slate-900 text-lg">{c.name}</div>
                    <Button
                      variant="ghost" size="icon"
                      className="h-7 w-7"
                      onClick={() => toggleStatus(c)}
                      data-testid={`class-delete-${c.school_class_id}`}
                      aria-label="Deactivate"
                    >
                      <Trash2 size={14} className="text-slate-500" />
                    </Button>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Grade {c.grade} · Section {c.section}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{c.academic_year}</div>
                  <Button
                    variant="outline" size="sm"
                    className="mt-3 w-full"
                    onClick={() => navigate(`/school-admin/classes/${c.school_class_id}/roster`)}
                    data-testid={`view-roster-btn-${c.school_class_id}`}
                  >
                    <Users size={12} className="mr-1.5" /> View Student List
                  </Button>
                </div>
              ))}
            </div>
          )}

          {inactive.length > 0 && (
            <section className="mt-6" data-testid="inactive-classes-section">
              <h3 className="text-sm font-semibold text-slate-500 mb-2">
                Inactive ({inactive.length})
              </h3>
              <div className="grid gap-2">
                {inactive.map((c) => (
                  <div
                    key={c.school_class_id}
                    className="bg-slate-50 border rounded-lg px-4 py-2 flex items-center justify-between opacity-70"
                    data-testid={`inactive-class-${c.school_class_id}`}
                  >
                    <div className="text-sm">
                      <span className="font-medium">{c.name}</span>
                      <span className="text-slate-500 ml-2">· {c.academic_year}</span>
                      <Badge variant="secondary" className="ml-2 text-xs">Inactive</Badge>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => toggleStatus(c)}
                      data-testid={`class-reactivate-${c.school_class_id}`}
                    >
                      <RotateCcw size={12} className="mr-1" /> Reactivate
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-class-dialog">
          <DialogHeader>
            <DialogTitle>Add Class</DialogTitle>
            <DialogDescription>
              Every class is identified by its grade, section, and academic year.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="class-grade">Grade *</Label>
                <Input
                  id="class-grade"
                  data-testid="class-grade-input"
                  placeholder="e.g., 9 or KG"
                  value={form.grade}
                  onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
                  maxLength={10}
                />
              </div>
              <div>
                <Label htmlFor="class-section">Section *</Label>
                <Input
                  id="class-section"
                  data-testid="class-section-input"
                  placeholder="A"
                  value={form.section}
                  onChange={(e) => setForm((f) => ({ ...f, section: e.target.value.toUpperCase() }))}
                  maxLength={5}
                />
              </div>
            </div>
            <div>
              <Label>Academic Year *</Label>
              <Select
                value={form.academic_year}
                onValueChange={(v) => setForm((f) => ({ ...f, academic_year: v }))}
              >
                <SelectTrigger data-testid="class-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACADEMIC_YEARS.map((ay) => (
                    <SelectItem key={ay} value={ay}>{ay}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline" onClick={() => setAddOpen(false)}
              disabled={saving}
              data-testid="add-class-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit} disabled={saving}
              style={{ backgroundColor: brandColor }}
              className="text-white"
              data-testid="add-class-submit"
            >
              {saving ? 'Saving…' : 'Add Class'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
